import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import { audioContextIsReadyToUse, Loading, PasswordInput, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toastError } from "../../../../helpers/toast";
import { useMeetingContext, useMeetingSelector, useMultiMeetingAPI } from "../../../../hooks/meeting";
import { SessionMode } from "../../../../store/session/slices";

import { ButtonFinal, Popup } from "@kalyzee/escaet-ui";
import { isMeetingSessionRoleGreaterThanOrEqual, MeetingContainer } from "@kalyzee/kast-web-sdk";
import { ApiErrorCode, MeetingJoinRequest, MeetingSessionRole } from "@kalyzee/kast-web-sdk/websocket";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";

export interface MeetingConnectorContextState {
  mode: "expert" | "student";
}

export const MeetingConnectorContext = React.createContext<MeetingConnectorContextState | undefined>(undefined);

export const useMeetingConnectorContext = (): MeetingConnectorContextState => {
  const context = React.useContext(MeetingConnectorContext);
  if (!context) {
    throw new Error("useMeetingConnectorContext must be used within a MeetingConnectorProvider");
  }
  return context;
};

export const MeetingConnector = ({ children }: { children: React.ReactNode }) => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const meetingCtx = useMeetingContext();
  const meetingCtxRef = useRefAutoUpdate(meetingCtx);
  const id = pageContext?.params?.id ?? "";

  const multiMeetingAPI = useMultiMeetingAPI(
    id, // Focused meeting ID
    true,
    true // only selected if no other running
  );
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);

  const [meeting] = useMeetingSelector(multiMeetingAPI.focusedMeetingAPI, (state) => state.meeting);
  const [session] = useMeetingSelector(multiMeetingAPI.focusedMeetingAPI, (state) => state.session);

  const [wall, setWall] = useState<"password_try" | "password_retry" | "already_connected">();
  const [password, setPassword] = useState<string | undefined>(undefined);
  const forceToConnectRef = useRef(false);

  const connectorState = useMemo<MeetingConnectorContextState | undefined>(() => {
    if (!session?.role) return undefined;
    let mode: "expert" | "student" = "student";
    if (isMeetingSessionRoleGreaterThanOrEqual(session.role, MeetingSessionRole.MODERATOR)) {
      mode = "expert";
    }
    return { mode };
  }, [session?.role]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const joinContainer = async (container: MeetingContainer, data?: Omit<MeetingJoinRequest, "id" | "disconnectFirstUserIfNeeded">) => {
    const containerAPI = container.getAPI();

    // Joining without media, expert and student pages are responsible for adding media
    const request: Omit<MeetingJoinRequest, "id"> = {...(data ?? {}), disconnectFirstUserIfNeeded: forceToConnectRef.current };
    const result = await containerAPI.join(request);

    if (result?.error?.code === -ApiErrorCode.MEETING_PASSWORD_IS_NEEDED) {
      setWall("password_try");
      return;
    } else if (result?.error?.code === -ApiErrorCode.MEETING_INVALID_PASSWORD) {
      setWall("password_retry");
      return;
    } else if (result?.error?.code === -ApiErrorCode.MEETING_ALREADY_JOINED_WITH_THIS_USER_ACCOUNT) {
      setWall("already_connected");
      return;
    } else {
      if (result.error) {
        console.error(`[ERROR] join: `, result.error);
        toastError(`Une erreur est apparue.`);
        pageContextRef.current?.goTo(PageMasterMode.LIST);
        container.destroy();
        return;
      }
      const session = result.response?.session;
      if (!session) {
        console.error(`[ERROR] join - internal error: session is undefined`);
        toastError(`Une erreur est apparue.`);
        pageContextRef.current?.goTo(PageMasterMode.LIST);
        container.destroy();
        return;
      }
    }
  };

  const createAndJoinContainer = async (id: string, data?: Omit<MeetingJoinRequest, "id">) => {
    const api = multiMeetingAPIRef.current;

    if (!api.all) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }

    const container = api.all.getOrCreateContainer(id);
    joinContainer(container, data);
  };

  const leave = async () => {
    const promises: Promise<any>[] = [];
    meetingCtxRef.current?.api?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // TODO : does it break anything on expert side ? didn't find what yet
  useEffect(() => {
    const containers = multiMeetingAPI?.all?.getContainers();
    containers?.forEach((container) => {
      if (container.getStatus() !== "disconnected") return;
      joinContainer(container);
    });
  }, [multiMeetingAPI.all]);

  useEffect(() => {
    audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer()).then((ready) => {
      if (!ready) {
        const queries: any = {};
        if (id) queries["meeting"] = id;
        pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries });
      } else {
        createAndJoinContainer(id);
      }
    });
    return () => {
      leave();
    };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderWall = () => {
    if (!wall) return null;

    if (wall === "password_try" || wall === "password_retry") {
      const onConfirm = () => {
        setWall(undefined);
        createAndJoinContainer(id, { password });
      };
  
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "radial-gradient(circle, #888888, #666666)",
          }}
        >
          <Popup
            style={{
              width: "fit-content",
              height: "fit-content",
              padding: "2em",
            }}
            Content={
              <div style={{ minWidth: "500px", zIndex: 15, fontFamily: "Nunito, sans-serif" }}>
                <div style={{ fontSize: "1.5rem", color: "black", marginBottom: "30px", fontWeight: 800, textAlign: "center", width: "100%" }}>
                  {wall === "password_retry" ? "Le mot de passe saisi est incorrect" : "Le meeting est protégé par un mot de passe"}
                </div>
                <div style={{ marginBottom: "5px" }}>Saisissez le mot de passe :</div>
                <PasswordInput
                  style={{ width: "100%", height: "30px", paddingLeft: "2px", borderRadius: "5px", border: "1px solid black", marginBottom: "15px" }}
                  value={password ?? ""}
                  setValue={(e) => {
                    setPassword(e);
                  }}
                  nativeProps={{
                    autoFocus: true,
                    onKeyDown: (e) => {
                      if (e.key === "Enter") {
                        onConfirm();
                      }
                    },
                  }}
                />
                <ButtonFinal
                  text="Valider"
                  textColor="#fff"
                  background="rgb(0, 144, 255)"
                  cx="!px-[15px] !py-[10px] !font-[800] !rounded-[5px] !h-[72px] !text-[24px] !w-[580px]"
                  onClick={() => {
                    onConfirm();
                  }}
                />
              </div>
            }
          />
        </div>
      );
    }

    if (wall === "already_connected") {
      const onBack = () => {
        setWall(undefined);
        pageContextRef.current?.goTo(PageMasterMode.LIST);
      };

      const onConfirm = () => {
        setWall(undefined);
        forceToConnectRef.current = true;
        createAndJoinContainer(id);
      };
  
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "radial-gradient(circle, #888888, #666666)",
          }}
        >
          <Popup
            style={{
              width: "fit-content",
              height: "fit-content",
              padding: "2em",
            }}
            Content={
              <div style={{ minWidth: "500px", zIndex: 15, fontFamily: "Nunito, sans-serif" }}>
                <div style={{ fontSize: "1.75rem", color: "#f05e5e", marginBottom: "5px", fontWeight: 800, textAlign: "center", width: "100%" }}>
                  Vous êtes déjà connecté avec ce compte
                </div>
                <div style={{ fontSize: "1.25rem", fontWeight: 800, textAlign: "center" }}>Voulez-vous vous tout de même vous connecter ?</div>
                <div style={{ marginBottom: "20px", textAlign: "center" }}>Cela déconnectera automatiquement l'autre appareil</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center', }}>
                  <ButtonFinal
                    text="Non, revenir à la page d'accueil"
                    textColor="rgb(130, 129, 127)"
                    background="rgb(241, 239, 235)"
                    cx="!px-[15px] !py-[10px] !font-[800] !rounded-[5px] !h-[72px] !text-[24px] !w-[100%]"
                    onClick={() => {
                      onBack();
                    }}
                  />
                  <ButtonFinal
                    text="Oui, forcer la connexion"
                    textColor="#fff"
                    background="rgb(0, 144, 255)"
                    cx="!px-[15px] !py-[10px] !font-[800] !rounded-[5px] !h-[72px] !text-[24px] !w-[100%]"
                    onClick={() => {
                      onConfirm();
                    }}
                  />
                </div>
              </div>
            }
          />
        </div>
      );
    }

    return null;
  };

  if (meeting && connectorState) {
    return <MeetingConnectorContext.Provider value={connectorState}>{children}</MeetingConnectorContext.Provider>;
  }

  if (wall) {
    return renderWall();
  }

  return <Loading fullscreen message="Chargement en cours" />;
};
